import React, { useId } from "react";
import clsx from "clsx";

export type RadioOption = {
  label: string;
  value: string;
  disabled?: boolean;
  disabledNote?: React.ReactNode;
};

export interface RadioGroupProps
  extends Omit<
    React.FieldsetHTMLAttributes<HTMLFieldSetElement>,
    "onChange" | "onBlur"
  > {
  name: string;
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  options: RadioOption[];
  id?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  layout?: "vertical" | "horizontal";
}

export const RadioGroup = React.forwardRef<
  HTMLFieldSetElement,
  RadioGroupProps
>(
  (
    {
      name,
      value,
      onChange,
      onBlur,
      options,
      disabled = false,
      id,
      className,
      layout = "vertical",
      "aria-invalid": ariaInvalid,
      "aria-describedby": ariaDescribedBy,
      ...fieldsetProps
    },
    ref
  ) => {
    const baseId = useId();
    const fieldsetId = id || baseId;

    return (
      <fieldset
        ref={ref}
        id={fieldsetId}
        className={clsx("flex flex-col gap-2", className)}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        {...fieldsetProps}
      >
        <div
          className={clsx(
            "flex",
            layout === "horizontal" ? "flex-row flex-wrap gap-2" : "flex-col gap-2"
          )}
        >
          {options.map((opt) => {
            const checked = String(value || "") === String(opt.value);
            const optionId = `${fieldsetId}-${opt.value}`;
            const isOptionDisabled = disabled || opt.disabled;

            return (
              <div key={opt.value} className="flex flex-col">
                <label
                  htmlFor={optionId}
                  className={clsx(
                    "flex items-center border rounded-md w-full transition-colors",
                    layout === "horizontal" 
                      ? "gap-2 px-3 py-2" 
                      : "gap-3 px-3 py-3",
                    isOptionDisabled
                      ? "bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200"
                      : "cursor-pointer bg-white border-gray-300 hover:bg-gray-50",
                    checked &&
                      !isOptionDisabled &&
                      "border-lifeguards-blue ring-1 ring-lifeguards-blue"
                  )}
                >
                <input
                  type="radio"
                  id={optionId}
                  name={name}
                  value={opt.value}
                  checked={checked}
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={isOptionDisabled}
                  aria-invalid={ariaInvalid}
                  aria-describedby={ariaDescribedBy}
                  className={clsx("peer sr-only")}
                />

                {/* Custom indicator so the filled dot matches button blue */}
                <span
                  aria-hidden="true"
                  className={clsx(
                    "flex items-center justify-center rounded-full border transition-colors shrink-0",
                    layout === "horizontal" ? "h-4 w-4" : "h-5 w-5",
                    checked ? "border-lifeguards-blue" : "border-gray-400",
                    !isOptionDisabled &&
                      "peer-focus-visible:ring-2 peer-focus-visible:ring-lifeguards-blue peer-focus-visible:ring-offset-2",
                    isOptionDisabled && "opacity-80"
                  )}
                >
                  <span
                    className={clsx(
                      "rounded-full transition-colors",
                      layout === "horizontal" ? "h-2 w-2" : "h-2.5 w-2.5",
                      checked ? "bg-lifeguards-blue" : "bg-transparent"
                    )}
                  />
                </span>

                <span className={clsx("text-sm")}>{opt.label}</span>
              </label>
              {isOptionDisabled && opt.disabledNote && (
                <p className="text-xs text-gray-600 italic ml-8 mt-1">
                  {opt.disabledNote}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </fieldset>
  );
});

RadioGroup.displayName = "RadioGroup";
